import * as React from 'react'
import { Outlet } from 'react-router-dom'

import { Form } from 'factorial-form'
import orderBy from 'lodash/orderBy'

import { observer } from 'mobx-react'

// Hook
import createMemo from 'modules/core/hooks/createMemo'
import { normalizeText } from 'modules/core/lib/stringUtils'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import type { DashboardAuthorInfo } from 'modules/reports/lib/types'
import dashboards from 'modules/reports/store/collections/dashboards'
import type DashboardModel from 'modules/reports/store/models/Dashboard'

import Content from './components/_Content'
import ListDashboards from './components/_ListDashboards'

export interface RenderProps {
  dashboard: DashboardModel
}
interface Props {
  loading: boolean
  canManageReports: boolean
  authorsByDashboardId: Map<string, DashboardAuthorInfo>
}

const useOrderDashboards = createMemo(
  (form, dashboards, authorsByDashboardId: Map<string, DashboardAuthorInfo>) => {
    const sortAttribute: string = form.get('sortAttribute').value
    const sortDirection: string = form.get('sortDirection').value

    return orderBy(
      dashboards,
      [
        (dashboard: DashboardModel): string => {
          if (sortAttribute === 'title') {
            return normalizeText(dashboard.attributes.title)
          }

          if (sortAttribute === 'author') {
            const author = dashboard.id ? authorsByDashboardId.get(dashboard.id) : undefined
            return normalizeText(
              author
                ? getFullDisplayName({
                    firstName: author.firstName,
                    lastName: author.lastName,
                  })
                : ''
            )
          }

          if (sortAttribute === 'created_at') {
            return dashboard.attributes.created_at
          }

          return dashboard.attributes.updated_at
        },
      ],
      [sortDirection as 'asc' | 'desc']
    )
  }
)

const DashboardList = ({ loading, canManageReports, authorsByDashboardId }: Props) => {
  const filtersForm = React.useMemo(
    () =>
      new Form(
        {
          sortAttribute: 'updated_at',
          sortDirection: 'desc',
        },
        {
          sortAttribute: 'string',
          sortDirection: 'string',
        }
      ),
    []
  )

  const dashboardList = useOrderDashboards(filtersForm, dashboards.toArray(), authorsByDashboardId)

  const showAddDashboardButton = !loading && canManageReports

  return (
    <ResponsivePage.Body error={undefined} loading={loading} showHeader={false}>
      <Content>
        <ListDashboards
          showAddDashboardButton={showAddDashboardButton}
          filtersForm={filtersForm}
          dashboards={dashboardList}
          canManageReports={canManageReports}
          authorsByDashboardId={authorsByDashboardId}
        />
      </Content>
      <Outlet />
    </ResponsivePage.Body>
  )
}

export default observer(DashboardList)
